import { useState, useEffect } from "react";
import { useAdmin } from "../context/AdminContext";
import { EntityTable, type Column } from "../components/EntityTable";
import type { Integration, Connection, ConnectionCredentials } from "../hook/useIntegratorAdmin";
import type { GitHubRepo } from "../hook/integrator/useGitHubAdmin";

type View = 'integrations' | 'connections' | 'credentials' | 'actions';

const LAST_INTEGRATOR_VIEW_KEY = 'admin-last-integrator-view';

export function IntegratorPanel() {
  const { integrator } = useAdmin();
  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_INTEGRATOR_VIEW_KEY) as View | null;
    return saved || 'integrations';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_INTEGRATOR_VIEW_KEY, view);
  }, [view]);
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');
  const [selectedIntegration, setSelectedIntegration] = useState<string>('');
  const [credentials, setCredentials] = useState<ConnectionCredentials | null>(null);
  const [maskedTokens, setMaskedTokens] = useState(true);
  
  // GitHub Actions state
  const [githubRepos, setGithubRepos] = useState<GitHubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [actionParticipant, setActionParticipant] = useState<string>('');

  // Generate OAuth URL and open popup
  const handleConnect = async (participantId: string, integrationId: string) => {
    try {
      const authUrl = await integrator.connections.createAuthUrl(participantId, integrationId);
      const popup = window.open(authUrl, 'oauth', 'width=600,height=700');
      
      // Poll for connection
      const checkInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(checkInterval);
          await integrator.connections.refresh();
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to create auth URL:', error);
      alert('Failed to initiate OAuth flow');
    }
  };

  // Delete connection
  const handleDelete = async (participantId: string, integrationId: string) => {
    if (!confirm(`Delete connection for ${participantId}?`)) return;
    try {
      await integrator.connections.deleteConnection(participantId, integrationId);
    } catch (error) {
      console.error('Failed to delete connection:', error);
      alert('Failed to delete connection');
    }
  };

  // Load credentials
  const handleShowCredentials = async (participantId: string, integrationId: string) => {
    try {
      const creds = await integrator.connections.getCredentials(participantId, integrationId);
      setCredentials(creds);
      setView('credentials');
      setMaskedTokens(true);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      alert('Failed to load credentials');
    }
  };

  // Integrations table columns
  const integrationColumns: Column<Integration>[] = [
    {
      key: 'unique_key',
      label: 'Integration ID',
      sortable: true,
      render: (i) => (
        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{i.unique_key}</span>
      ),
    },
    {
      key: 'provider',
      label: 'Provider',
      sortable: true,
      render: (i) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">{i.provider}</span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (i) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(i.created_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  // Connections table columns
  const connectionColumns: Column<Connection>[] = [
    {
      key: 'participantId',
      label: 'Participant',
      sortable: true,
      render: (c) => (
        <span className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate max-w-xs block">
          {c.participantId}
        </span>
      ),
    },
    {
      key: 'integrationId',
      label: 'Integration',
      sortable: true,
      render: (c) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.integrationId}</span>
      ),
    },
    {
      key: 'provider',
      label: 'Provider',
      sortable: true,
      render: (c) => (
        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{c.provider}</span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Connected',
      sortable: true,
      render: (c) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(c.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (c) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleShowCredentials(c.participantId, c.integrationId)}
            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Credentials
          </button>
          <button
            onClick={() => handleDelete(c.participantId, c.integrationId)}
            className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const maskToken = (token: string) => {
    if (!maskedTokens) return token;
    return token.substring(0, 8) + '...' + token.substring(token.length - 8);
  };

  // Load GitHub repos for participant
  const loadGitHubRepos = async (participantId: string) => {
    setLoadingRepos(true);
    try {
      const repos = await integrator.github.listRepos(participantId);
      setGithubRepos(repos);
    } catch (error) {
      console.error('Failed to load GitHub repos:', error);
      alert('Failed to load repositories. Make sure the participant has a GitHub connection.');
    } finally {
      setLoadingRepos(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {(['integrations', 'connections', 'credentials', 'actions'] as View[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                view === v
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Integrations View */}
      {view === 'integrations' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Available Integrations
            </h2>
            <button
              onClick={() => integrator.integrations.refresh()}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Refresh
            </button>
          </div>

          {integrator.integrations.error && (
            <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
              {integrator.integrations.error}
            </div>
          )}

          <EntityTable
            data={integrator.integrations.integrations}
            columns={integrationColumns}
            loading={integrator.integrations.loading}
            emptyMessage="No integrations configured in Nango"
          />
        </div>
      )}

      {/* Connections View */}
      {view === 'connections' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Active Connections
            </h2>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Participant ID"
                value={selectedParticipant}
                onChange={(e) => setSelectedParticipant(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
              />
              <input
                type="text"
                placeholder="Integration ID"
                value={selectedIntegration}
                onChange={(e) => setSelectedIntegration(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button
                onClick={() => selectedParticipant && selectedIntegration && handleConnect(selectedParticipant, selectedIntegration)}
                disabled={!selectedParticipant || !selectedIntegration}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Connect
              </button>
              <button
                onClick={() => integrator.connections.refresh()}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Refresh
              </button>
            </div>
          </div>

          {integrator.connections.error && (
            <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
              {integrator.connections.error}
            </div>
          )}

          <EntityTable
            data={integrator.connections.connections}
            columns={connectionColumns}
            loading={integrator.connections.loading}
            emptyMessage="No connections found"
          />
        </div>
      )}

      {/* Credentials View */}
      {view === 'credentials' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Connection Credentials
            </h2>
            <button
              onClick={() => setView('connections')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Back
            </button>
          </div>

          {credentials ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Participant ID</label>
                <div className="mt-1 font-mono text-sm text-gray-900 dark:text-gray-100">{credentials.participantId}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Integration ID</label>
                <div className="mt-1 font-mono text-sm text-gray-900 dark:text-gray-100">{credentials.integrationId}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Connection ID</label>
                <div className="mt-1 font-mono text-sm text-gray-900 dark:text-gray-100">{credentials.connectionId}</div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Credentials</label>
                  <button
                    onClick={() => setMaskedTokens(!maskedTokens)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    {maskedTokens ? 'Reveal' : 'Mask'}
                  </button>
                </div>
                <div className="space-y-2">
                  {Object.entries(credentials.credentials).map(([key, value]) => {
                    const displayValue = typeof value === 'string' 
                      ? (value.length > 20 ? maskToken(value) : value)
                      : JSON.stringify(value, null, 2);
                    const copyValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                    
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-32">{key}:</span>
                        <code className="flex-1 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono text-gray-900 dark:text-gray-100 break-all whitespace-pre-wrap">
                          {displayValue}
                        </code>
                        <button
                          onClick={() => copyToClipboard(copyValue)}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Copy
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              Select a connection from the Connections tab to view credentials
            </div>
          )}
        </div>
      )}

      {/* Actions View - GitHub Example */}
      {view === 'actions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              GitHub Actions (Example)
            </h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Proxy actions through Nango using participant connections.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Participant ID"
                value={actionParticipant}
                onChange={(e) => setActionParticipant(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button
                onClick={() => actionParticipant && loadGitHubRepos(actionParticipant)}
                disabled={!actionParticipant || loadingRepos}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loadingRepos ? 'Loading...' : 'List Repos'}
              </button>
            </div>

            {githubRepos.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Repositories ({githubRepos.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {githubRepos.map(repo => (
                    <div
                      key={repo.id}
                      className="p-3 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <a
                            href={repo.html_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {repo.full_name}
                          </a>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {repo.description || 'No description'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          repo.private 
                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        }`}>
                          {repo.private ? 'Private' : 'Public'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Available Methods
              </h3>
              <div className="text-xs font-mono space-y-1 text-gray-600 dark:text-gray-400">
                <div>• integrator.github.listRepos(participantId)</div>
                <div>• integrator.github.getRepo(participantId, owner, repo)</div>
                <div>• integrator.github.listIssues(participantId, owner, repo)</div>
                <div>• integrator.github.createIssue(participantId, owner, repo, title, body)</div>
                <div>• integrator.github.proxyRequest(participantId, endpoint, method, data)</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

